import { NativeEvent, ScreenViewEvent } from '~/shared/NativeBridge';

/**
 * Screen tracking do app nativo (ENG-402).
 *
 * Dentro da WebView o gtag/GTM não roda (política ENG-397), então o GA4 não
 * recebe navegação do app — o Firebase só reporta `MainActivity`. Este plugin
 * reconstrói o que a Medição Otimizada faz no site: a cada troca de rota,
 * despacha a rota pela bridge e o app loga `screen_view` no GA4F (padrão
 * recomendado pelo Google para WebViews — firebase.google.com/docs/analytics/webview).
 *
 * Usa `to.path` (sem query string) para não vazar click-ids/PII, com dedupe
 * de paths consecutivos (mudança só de query não reenvia). O `afterEach`
 * também cobre a navegação inicial da hydration. Fora do app, no-op.
 *
 * O `screen_name` leva o `document.title` (par do "título da página" do
 * stream web), mas o unhead só escreve o título novo no DOM depois do
 * `afterEach` — então o dispatch espera o título mudar (ou um teto de
 * frames, para páginas de título igual) antes de ler. Se outra navegação
 * chegar durante a espera, a pendente é descartada — nunca sai tela com
 * título da página errada.
 */

/** Teto da espera pelo título novo (~250ms @ 60fps). */
const TITLE_WAIT_MAX_FRAMES = 15;

export default defineNuxtPlugin(() => {
  if (!NativeEvent.isNativeApp()) {
    return;
  }

  let lastPath: string | null = null;

  const dispatchWhenTitleSettles = (
    path: string,
    prevTitle: string,
    frame = 0,
  ): void => {
    if (path !== lastPath) {
      // navegação mais nova em andamento — descarta a pendente
      return;
    }

    if (document.title !== prevTitle || frame >= TITLE_WAIT_MAX_FRAMES) {
      new ScreenViewEvent(path, document.title).dispatch();

      return;
    }

    requestAnimationFrame(() =>
      dispatchWhenTitleSettles(path, prevTitle, frame + 1),
    );
  };

  useRouter().afterEach((to, _from, failure) => {
    if (failure || to.path === lastPath) {
      return;
    }

    const isFirstNavigation = lastPath === null;
    const prevTitle = document.title;

    lastPath = to.path;

    if (isFirstNavigation) {
      // hydration: o título já veio correto do SSR, não há o que esperar
      new ScreenViewEvent(to.path, document.title).dispatch();

      return;
    }

    dispatchWhenTitleSettles(to.path, prevTitle);
  });
});
